import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  inventoryService, 
  Inventory, 
  InventoryCategory, 
  CreateInventoryData, 
  CreateInventoryCategoryData, 
  UpdateInventoryData 
} from '../../services/inventoryService';

interface InventoryState {
  inventories: Inventory[];
  categories: InventoryCategory[];
  selectedInventory: Inventory | null;
  loading: boolean;
  error: string | null;
  categoriesLoading: boolean;
  categoriesError: string | null;
}

const initialState: InventoryState = {
  inventories: [],
  categories: [],
  selectedInventory: null,
  loading: false,
  error: null,
  categoriesLoading: false,
  categoriesError: null,
};

// Inventory Category Thunks
export const createInventoryCategory = createAsyncThunk(
  'inventory/createCategory',
  async (data: CreateInventoryCategoryData, { rejectWithValue }) => {
    try {
      const result = await inventoryService.createInventoryCategory(data);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create inventory category');
    }
  }
);

export const fetchInventoryCategories = createAsyncThunk(
  'inventory/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const result = await inventoryService.getInventoryCategories();
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inventory categories');
    }
  }
);

export const deleteInventoryCategory = createAsyncThunk(
  'inventory/deleteCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await inventoryService.deleteInventoryCategory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete inventory category');
    }
  }
);

// Inventory Thunks
export const createInventory = createAsyncThunk(
  'inventory/create',
  async (data: CreateInventoryData, { rejectWithValue }) => {
    try {
      const result = await inventoryService.createInventory(data);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create inventory item');
    }
  }
);

export const fetchInventories = createAsyncThunk(
  'inventory/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const result = await inventoryService.getInventories();
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inventories');
    }
  }
);

export const fetchInventory = createAsyncThunk(
  'inventory/fetchOne',
  async (id: string, { rejectWithValue }) => {
    try {
      return await inventoryService.getInventory(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inventory item');
    }
  }
);

export const updateInventory = createAsyncThunk(
  'inventory/update',
  async ({ id, data }: { id: string; data: UpdateInventoryData }, { rejectWithValue }) => {
    try {
      const result = await inventoryService.updateInventory(id, data);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update inventory item');
    }
  }
);

export const deleteInventory = createAsyncThunk(
  'inventory/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await inventoryService.deleteInventory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete inventory item');
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.categoriesError = null;
    },
    clearSelectedInventory: (state) => {
      state.selectedInventory = null;
    },
  },
  extraReducers: (builder) => {
    // Category cases
    builder
      .addCase(createInventoryCategory.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(createInventoryCategory.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories.push(action.payload);
      })
      .addCase(createInventoryCategory.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload as string;
      })
      .addCase(fetchInventoryCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchInventoryCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchInventoryCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload as string;
      })
      .addCase(deleteInventoryCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(cat => cat._id !== action.payload);
      })
      // Inventory cases
      .addCase(createInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.inventories.push(action.payload);
      })
      .addCase(createInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInventories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventories.fulfilled, (state, action) => {
        state.loading = false;
        state.inventories = action.payload;
      })
      .addCase(fetchInventories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedInventory = action.payload;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInventory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.inventories.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.inventories[index] = action.payload;
        }
        if (state.selectedInventory?._id === action.payload._id) {
          state.selectedInventory = action.payload;
        }
      })
      .addCase(updateInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteInventory.fulfilled, (state, action) => {
        state.inventories = state.inventories.filter(item => item._id !== action.payload);
        if (state.selectedInventory?._id === action.payload) {
          state.selectedInventory = null;
        }
      });
  },
});

export const { clearError, clearSelectedInventory } = inventorySlice.actions;
export default inventorySlice.reducer;